import React, { useEffect, useState, useCallback } from "react";
import { t } from "i18next";
import CustomButton from "ui/Button";
import { COLOR } from "utils/constant";
import {
	adminChangePasswordApi,
} from "api/auth";
import { CustomError } from "interface";
import { useInfoStore } from "store/error";
import { PasswordInput } from "ui/Input";
import { useParams } from "react-router";
import { AxiosApi } from "api";

interface PasswordRules {
	minLength: boolean;
	hasUpper: boolean;
	hasLower: boolean;
	hasDigit: boolean;
	hasSpecial: boolean;
}

const validatePassword = (password: string): PasswordRules => ({
	minLength: password.length >= 6,
	hasUpper: /[A-Z]/.test(password),
	hasLower: /[a-z]/.test(password),
	hasDigit: /\d/.test(password),
	hasSpecial: /[!@#$%^&*()_+{}|;:,.<>?\[\]]/.test(password),
});

const generateSecurePassword = (length: number = 12): string => {
	const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
	const lowercase = "abcdefghijklmnopqrstuvwxyz";
	const digits = "0123456789";
	const special = "!@#$%^&*_<>?";

	let password = "";
	password += uppercase[Math.floor(Math.random() * uppercase.length)];
	password += lowercase[Math.floor(Math.random() * lowercase.length)];
	password += digits[Math.floor(Math.random() * digits.length)];
	password += special[Math.floor(Math.random() * special.length)];

	const allChars = uppercase + lowercase + digits + special;
	for (let i = password.length; i < length; i++) {
		password += allChars[Math.floor(Math.random() * allChars.length)];
	}

	return password
		.split("")
		.sort(() => Math.random() - 0.5)
		.join("");
};

export const AdminChangePassword: React.FC = () => {
	const [form, setForm] = useState({
		newPassword: "",
		confirmPassword: "",
	});
	const [error, setError] = useState<string>("");
	const [passwordChanged, setPasswordChanged] = useState<boolean>(false);
	const [isGeneratingPassword, setIsGeneratingPassword] =
		useState<boolean>(false);
	const [rules, setRules] = useState<PasswordRules>({
		minLength: false,
		hasUpper: false,
		hasLower: false,
		hasDigit: false,
		hasSpecial: false,
	});

	const { setError: setErrorInfo, setInfo, resetStore } = useInfoStore();
	let { id } = useParams();
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const onNewPasswordChange = useCallback((value: string) => {
		setForm((prev) => ({ ...prev, newPassword: value }));
		setRules(validatePassword(value));
	}, []);

	// Generate password and auto-fill
	const handleGeneratePassword = () => {
		const generatedPassword = generateSecurePassword(12);
		setForm((prev) => ({
			...prev,
			newPassword: generatedPassword,
			confirmPassword: generatedPassword,
		}));
		setRules(validatePassword(generatedPassword));
		setError("");
	};

	const handleGenerateAndSendPassword = async () => {
		if (!id) {
			setError(t("UserNotFound"));
			return;
		}

		setIsGeneratingPassword(true);

		try {
			await AxiosApi.post("/Auth/AdminCreateUserPassword", {
				userId: id,
			});

			setInfo({
				title: t("Success"),
				description: t("PasswordGeneratedAndSentSuccess"),
				show: true,
				infoType: "Success",
				actions: [
					{
						text: t("Ok"),
						btn: () => resetStore(),
						bgColor: COLOR.Green,
						textColor: "white",
					},
				],
			});
			setForm({
				newPassword: "",
				confirmPassword: "",
			});
			setRules({
				minLength: false,
				hasUpper: false,
				hasLower: false,
				hasDigit: false,
				hasSpecial: false,
			});
		} catch (error: any) {
			const err = new CustomError(
				error.response?.data?.message || t("PasswordGenerationFailed")
			);
			setErrorInfo(err, true);
			setError(t("PasswordGenerationFailed"));
		} finally {
			setIsGeneratingPassword(false);
		}
	};

	const handleSubmit = () => {
		if (!form.newPassword || !form.confirmPassword) {
			setError(t("AllFieldsRequired"));
			return;
		}
		if (form.newPassword !== form.confirmPassword) {
			setError(t("PasswordMismatch"));
			return;
		}
		const currentRules = validatePassword(form.newPassword);
		if (
			!currentRules.minLength ||
			!currentRules.hasUpper ||
			!currentRules.hasLower ||
			!currentRules.hasDigit ||
			!currentRules.hasSpecial
		) {
			setError(t("PasswordRequirementsNotMet"));
			return;
		}
		setError("");
		adminChangePasswordApi({
			password: form.newPassword,
			userId: id as any,
		})
			.then(() => {
				setForm({
					newPassword: "",
					confirmPassword: "",
				});
				setInfo({
					title: t("Success"),
					description: t("ChangePasswordSuccess"),
					show: true,
					actions: [
						{
							text: t("Ok"),
							btn: () => resetStore(),
							bgColor: COLOR.Green,
							textColor: "white",
						},
					],
					infoType: "Success",
				});
				setPasswordChanged(true);
			})
			.catch((error) => {
				const err = new CustomError(
					t("PasswordChangeFailed", "PasswordChangeFailed")
				);
				const showCustomError = true;
				setErrorInfo(err, showCustomError);
				setError(t("PasswordChangeFailed"));
			});
	};

	const renderRuleItem = (label: string, passed: boolean) => (
		<div style={{ color: passed ? "green" : "red", marginBottom: "4px" }}>
			{passed ? "✔" : "✖"} {label}
		</div>
	);

	return (
		<div className="user-information-password" style={{ width: "100%", maxWidth: "1500px" }}>
			<div className="fontWork22">{t("ChangePassword")}</div>
			{error && <div style={{ color: "red" }}>{error}</div>}
			<div
				className="user-information-password-layout"
				style={{
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: isMobileView ? "20px" : "100px",
				}}
			>
				<div style={{ flex: 1 }}>
					<div style={{ marginTop: "10px" }}>
						<label>{t("NewPassword")}</label>
						<PasswordInput
							containerStyle={{ width: "100%" }}
							value={form.newPassword}
							onChange={onNewPasswordChange}
							setPasswordValidated={() => {}}
						/>
						{/* Password generation buttons */}
						<div
							style={{
								display: "flex",
								gap: "10px",
								marginTop: "8px",
							}}
						>
							<CustomButton
								label={t("GeneratePassword")}
								bgColor={COLOR.Green}
								color="white"
								onClick={handleGeneratePassword}
							/>
						</div>
					</div>
					<div style={{ marginTop: "10px" }}>
						<label>{t("ConfirmNewPassword")}</label>
						<PasswordInput
							containerStyle={{ width: "100%" }}
							value={form.confirmPassword}
							onChange={(value) =>
								setForm((prev) => ({ ...prev, confirmPassword: value }))
							}
							setPasswordValidated={() => {}}
						/>
					</div>
				</div>
				<div style={{ width: isMobileView ? "100%" : "300px", paddingTop: isMobileView ? "0" : "35px" }}>
					{renderRuleItem(t("PasswordAtleast"), rules.minLength)}
					{renderRuleItem(t("PasswordUppercase"), rules.hasUpper)}
					{renderRuleItem(t("PasswordLowercase"), rules.hasLower)}
					{renderRuleItem(t("PasswordDigit"), rules.hasDigit)}
					{renderRuleItem(t("PasswordSpecial"), rules.hasSpecial)}
				</div>
			</div>
			<div
				className="user-information-password-actions"
				style={{
					marginTop: "20px",
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: isMobileView ? "12px" : "40px",
				}}
			>
				<CustomButton
					label={t("ChangePassword")}
					bgColor={COLOR.Green}
					color="white"
					onClick={handleSubmit}
					btnStyle={isMobileView ? { width: "100%" } : undefined}
				/>
				<CustomButton
					label={isGeneratingPassword ? t("Generating...") : t("ChangeAndSend")}
					bgColor={COLOR.Green}
					color="white"
					onClick={handleGenerateAndSendPassword}
					disabled={isGeneratingPassword}
					btnStyle={isMobileView ? { width: "100%" } : undefined}
				/>
			</div>
			{passwordChanged && (
				<div style={{ color: "green", textAlign: "center" }}>
					{t("ChangePasswordSuccess")}
				</div>
			)}
		</div>
	);
};
